import { useState } from 'react';
import MainLayout from 'components/v2/layouts/main-layout';
import LeftBarSection from 'components/v2/modules/left-bar/left-bar-section';
import {
  TabButton,
  TabContent,
  Tabs,
  TabsContainer,
} from 'components/v2/molecules/tabs';
import SuggestedCreatorsWidgetMobile from 'components/v2/organisms/suggested-creators-widget-mobile';
import TopicHeader from 'components/v2/organisms/topic-header';
import TopicWidgetMobile from 'components/v2/organisms/topic-widget-mobile';
import { useInfiniteQuery } from 'react-query';
import {
  getTopicRelatedPosts,
  TopicRelatedPostsResult,
  TopicRelatedPostsTypeEnum,
} from 'service/post_topic';
import useGlobalStore from 'store/global';

import TopicPostsSection from './topic-posts-section';
import TopicRightSidebar from './topic-right-sidebar';

interface PostTopicPageProps {
  topicName?: string;
}

const PostTopicPage = ({ topicName = '日経平均' }: PostTopicPageProps) => {
  const isAuthInit = useGlobalStore((state) => state.isAuthInit);
  const [currentTab, setCurrentTab] = useState<TopicRelatedPostsTypeEnum>(
    TopicRelatedPostsTypeEnum.Latest,
  );

  const scrollToTop = () => {
    window.scrollTo(0, 0);
  };

  const getNextPageParam = (
    lastPage: TopicRelatedPostsResult,
    pages: TopicRelatedPostsResult[],
  ) => {
    const posts = lastPage?.data?.posts;
    if (posts && posts.length > 0) {
      return pages.length + 1;
    }
  };

  const { data, isFetched } = useInfiniteQuery(
    ['topic-posts', topicName],
    async ({ pageParam = 1 }) => {
      if (!isAuthInit) return Promise.reject();
      return getTopicRelatedPosts({
        topic: topicName,
        page: pageParam as number,
        type: TopicRelatedPostsTypeEnum.Latest,
      });
    },
    {
      getNextPageParam,
      enabled: isAuthInit,
      refetchOnWindowFocus: false,
      retry: false,
      staleTime: 15 * 60 * 1000,
      cacheTime: 15 * 60 * 1000,
    },
  );

  const firstPageData = data?.pages?.[0] || null;
  const topics = firstPageData?.data?.related_topics || [];
  const contributors = firstPageData?.data?.top_contributors || [];
  const mainTopic = firstPageData?.data?.main_topic || null;
  return (
    <MainLayout>
      <div className="grid grid-cols-mainMd grid-rows-mainMd grid-areas-mainMd lg:grid-cols-mainLg lg:grid-rows-mainLg lg:grid-areas-mainLg xl:grid-cols-mainXl xl:grid-rows-mainXl xl:grid-areas-mainXl">
        <LeftBarSection />
        <main className="mx-auto w-full min-w-0 max-w-full px-3 pb-20 pt-4 grid-in-mainContent md:w-[600px] md:max-w-[600px] md:px-5">
          {/* Topic Header */}
          {mainTopic && (
            <TopicHeader
              topicName={mainTopic.name}
              postCount={mainTopic?.post_count || 0}
              topicId={mainTopic.id}
              isFollow={firstPageData?.data?.is_follow || false}
            />
          )}

          {/* Filter Tabs */}
          <Tabs
            defaultTab={currentTab}
            onTabChange={(tab) => {
              setCurrentTab(tab as TopicRelatedPostsTypeEnum);
              scrollToTop();
            }}
          >
            <div className="sticky top-16 z-50 -mx-3 mb-4 shadow-sm md:mx-0">
              <TabsContainer>
                <TabButton tabKey={TopicRelatedPostsTypeEnum.Latest}>
                  最新
                </TabButton>
                <TabButton tabKey={TopicRelatedPostsTypeEnum.Prime}>
                  プライム
                </TabButton>
              </TabsContainer>
            </div>

            <div className="lg:hidden">
              <TopicWidgetMobile
                topics={topics}
                isFetched={isFetched}
                title="関連トピック"
                className="mb-4"
              />
              <SuggestedCreatorsWidgetMobile
                creators={contributors}
                title="トップコントリビューター"
                className="mb-4"
              />
            </div>
            <div className="overflow-x-hidden">
              <TabContent tabKey={TopicRelatedPostsTypeEnum.Latest}>
                <TopicPostsSection
                  topicName={topicName}
                  type={TopicRelatedPostsTypeEnum.Latest}
                />
              </TabContent>

              <TabContent tabKey={TopicRelatedPostsTypeEnum.Prime}>
                <TopicPostsSection
                  topicName={topicName}
                  type={TopicRelatedPostsTypeEnum.Prime}
                />
              </TabContent>
            </div>
          </Tabs>
        </main>

        {/* Right Sidebar */}
        <TopicRightSidebar
          topics={topics}
          contributors={contributors}
          isFetched={isFetched}
        />
      </div>
    </MainLayout>
  );
};

export default PostTopicPage;
